import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";

const PROPERTY_TYPES = [
  "Residential (Terraced / Semi / Detached)",
  "Commercial Premises",
  "Landlord / Rental Portfolio",
];

export default function BookingForm({ services, content }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState([]);
  const [propertyType, setPropertyType] = useState(PROPERTY_TYPES[0]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [fields, setFields] = useState({ name: "", phone: "", email: "", address: "", postcode: "", notes: "" });
  const [availability, setAvailability] = useState(null);
  const [sending, setSending] = useState(null); // "booking" | "quote" | null

  useEffect(() => {
    const pre = searchParams.get("service");
    if (pre) {
      const match = services.find((s) => s.slug === pre);
      if (match) setSelected([match.name]);
    }
  }, [searchParams, services]);

  useEffect(() => {
    api.get("/availability").then((r) => setAvailability(r.data)).catch(() => setAvailability({ days: {} }));
  }, []);

  const availableDays = availability?.days || {};

  const toggleService = (name) =>
    setSelected((cur) => (cur.includes(name) ? cur.filter((s) => s !== name) : [...cur, name]));

  const isDisabled = (d) => {
    if (!availability) return false;
    return !availableDays[format(d, "yyyy-MM-dd")];
  };

  const submit = async (mode) => {
    if (selected.length === 0) return toast.error("Please select at least one service.");
    if (mode === "booking" && !date) return toast.error("Please choose a preferred date.");
    if (!fields.name || !fields.phone || !fields.email) return toast.error("Please fill in your name, phone and email.");
    setSending(mode);
    try {
      const { data } = await api.post("/bookings", {
        services: selected,
        property_type: propertyType,
        date: date ? format(date, "yyyy-MM-dd") : null,
        slot: time || null,
        ...fields,
        mode,
      });
      toast.success(mode === "quote" ? "Quote request sent!" : "Booking received!");
      navigate(`/booking/confirmation/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(null);
    }
  };

  const inputCls =
    "w-full bg-[#0b1322] border border-white/15 rounded-xl px-4 py-3 text-sm text-white focus:border-[#1e9bff] outline-none transition-colors";
  const labelCls = "block text-xs font-mono-lbl uppercase tracking-wider text-[#1e9bff] mb-3";

  return (
    <section id="booking" className="bg-[#0b1322] text-[#f0f4f8] py-24 md:py-32 border-b border-[#1e9bff]/15" data-testid="booking-section">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="text-xs font-mono-lbl uppercase tracking-[0.2em] text-[#1e9bff] block mb-2">Online Booking Engine</span>
          <h2 className="font-display uppercase text-white text-[clamp(32px,4vw,52px)] leading-none mb-4">
            Book a Clean or Free Quote
          </h2>
          <p className="text-sm text-[#93b4d4]">
            Select your services, choose a slot, and receive instant confirmation to your email.
          </p>
        </div>

        <form
          className="p-6 md:p-10 rounded-2xl bg-[#070b12] border border-[#1e9bff]/40 shadow-[0_0_50px_rgba(30,155,255,0.15)] space-y-8"
          onSubmit={(e) => e.preventDefault()}
          data-testid="booking-form"
        >
          {/* 1. Services */}
          <fieldset>
            <legend className={labelCls}>1. Select Services (Multi-select)</legend>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs font-mono-lbl">
              {services.map((s) => (
                <label
                  key={s.slug}
                  data-testid={`booking-service-${s.slug}`}
                  className={`p-3 rounded-xl border flex items-center gap-2 cursor-pointer transition-colors ${
                    selected.includes(s.name)
                      ? "border-[#1e9bff] bg-[#1e9bff]/15 text-white"
                      : "border-white/15 bg-white/5 text-[#c8d9ea] hover:border-[#1e9bff]"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="accent-[#1e9bff]"
                    checked={selected.includes(s.name)}
                    onChange={() => toggleService(s.name)}
                  />
                  <span>{s.name}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* 2. Property type */}
          <fieldset>
            <legend className={labelCls}>2. Property Type</legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs font-mono-lbl">
              {PROPERTY_TYPES.map((p) => (
                <button
                  type="button"
                  key={p}
                  data-testid={`booking-property-${p.split(" ")[0].toLowerCase()}`}
                  onClick={() => setPropertyType(p)}
                  className={`p-3 rounded-xl border transition-colors ${
                    propertyType === p
                      ? "border-[#1e9bff] bg-[#1e9bff]/15 text-white"
                      : "border-white/15 bg-white/5 text-[#c8d9ea] hover:border-[#1e9bff]"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </fieldset>

          {/* 3. Date & time */}
          <fieldset>
            <legend className={labelCls}>3. Preferred Date &amp; Time</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-xl border border-white/15 bg-[#0b1322] p-3 flex justify-center" data-testid="booking-calendar">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={[{ before: new Date(Date.now() + 86400000) }, isDisabled]}
                  className="text-white"
                />
              </div>
              <div>
                <p className="text-xs text-[#93b4d4] mb-3">
                  {date ? `Time for ${format(date, "EEEE d MMMM")}` : "Pick an available date, then choose a time."}
                </p>
                <label className="block text-[10px] font-mono-lbl uppercase tracking-widest text-[#1e9bff] mb-1">Preferred time</label>
                <input
                  type="time"
                  value={time}
                  data-testid="booking-time-input"
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full bg-[#0b1322] border border-white/15 rounded-xl px-4 py-3 text-sm text-white focus:border-[#1e9bff] outline-none transition-colors [color-scheme:dark]"
                />
                <p className="text-[11px] text-[#627d98] mt-3">Choose any date and time that suits you — we'll confirm the exact arrival window by email or phone. Requesting a free quote? You can skip the date entirely.</p>
              </div>
            </div>
          </fieldset>

          {/* 4. Contact details */}
          <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <legend className={`${labelCls} col-span-full`}>4. Your Details</legend>
            <input required placeholder="Full name" data-testid="booking-name-input" className={inputCls}
              value={fields.name} onChange={(e) => setFields({ ...fields, name: e.target.value })} />
            <input required type="tel" placeholder="Phone number" data-testid="booking-phone-input" className={inputCls}
              value={fields.phone} onChange={(e) => setFields({ ...fields, phone: e.target.value })} />
            <input required type="email" placeholder="Email address" data-testid="booking-email-input" className={inputCls}
              value={fields.email} onChange={(e) => setFields({ ...fields, email: e.target.value })} />
            <input placeholder="Postcode (e.g. S11 8XX)" data-testid="booking-postcode-input" className={inputCls}
              value={fields.postcode} onChange={(e) => setFields({ ...fields, postcode: e.target.value })} />
            <input placeholder="Street address" data-testid="booking-address-input" className={`${inputCls} sm:col-span-2`}
              value={fields.address} onChange={(e) => setFields({ ...fields, address: e.target.value })} />
            <textarea rows={3} placeholder="Notes — access, water tap, anything we should know" data-testid="booking-notes-input"
              className={`${inputCls} sm:col-span-2`}
              value={fields.notes} onChange={(e) => setFields({ ...fields, notes: e.target.value })} />
          </fieldset>

          {/* 5. Submit */}
          <div className="pt-4 border-t border-white/10 flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={() => submit("booking")}
              disabled={!!sending}
              data-testid="booking-submit-book"
              className="flex-1 py-4 rounded-xl bg-[#1e9bff] text-black font-display uppercase text-lg tracking-wider hover:bg-[#60baff] transition-colors disabled:opacity-50"
            >
              {sending === "booking" ? "Booking…" : "Book a Clean"}
            </button>
            <button
              type="button"
              onClick={() => submit("quote")}
              disabled={!!sending}
              data-testid="booking-submit-quote"
              className="flex-1 py-4 rounded-xl border border-[#1e9bff]/50 text-[#1e9bff] bg-[#1e9bff]/10 font-display uppercase text-lg tracking-wider hover:bg-[#1e9bff]/20 transition-colors disabled:opacity-50"
            >
              {sending === "quote" ? "Sending…" : "Request Free Quote"}
            </button>
          </div>
          <p className="text-center text-xs font-mono-lbl text-[#93b4d4]">
            Instant confirmation sent to you &amp; <span className="text-white">{content?.email}</span>
          </p>
        </form>
      </div>
    </section>
  );
}
